import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut, Bot, Shield, IdCard, Settings, Briefcase, MessagesSquare } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { useAuth } from '../lib/auth';
import { LEXI_ENABLED, PUBLIC_SIGN_IN_VISIBLE } from '../lib/features';
import { useT } from '../i18n';
import { ThemeToggle } from './ThemeToggle';

const Navbar = () => {
  const { t } = useT();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  // All 9 paths in one place — matches the homepage grid + dashboard tiles.
  const pathwaysLinks = [
    { title: 'Career Clarity', href: '/career-clarity', desc: 'Confused after 10th or 12th? Short fit-tests give a clear direction.' },
    { title: 'Inner Specializations', href: '/specializations', desc: 'B.Tech / BBA / MBA — peek inside every branch before you decide.' },
    { title: 'Future-Ready Job Roles', href: '/job-roles', desc: 'Final year or switching? Roles that will still be there in 5 years.' },
    { title: 'Future AI Agent Pathway', href: '/ai-pathway', desc: 'New AI-native careers being created right now — across industries.' },
    { title: 'Banking Careers', href: '/banking', desc: 'SBI, IBPS, RBI — the stable, respected path, honestly.' },
    { title: 'Blue-Collar Jobs', href: '/blue-collar', desc: 'Skilled trades that pay — salon, repairs, delivery, own business path.' },
    { title: 'Entrepreneurship · OwnPath', href: '/ownpath', desc: 'Real Indian business ideas matched to your budget + govt schemes.' },
    { title: 'Build Badges', href: '/build-badges', desc: 'Ship a real project per role, paste GitHub, earn a verified badge.' },
    { title: 'Is AI Coming for Your Role?', href: '/ai-risk-check', desc: '30-second honest check — what stays human + skills to add now.' },
    { title: 'Life Skills — For Every Future', href: '/life-skills', desc: 'Practical skills to earn, adapt and grow — with honest income, risks and dignity.' },
    { title: 'The AI Shift — Prepare Now', href: '/the-ai-shift', desc: 'AI • AGI • ASI: understand the forecasts, follow a 2026–2032 preparation plan and build practical options.' },
    { title: 'Agriculture', href: '/agriculture', desc: 'Explore agricultural work, learning and possible futures.' },
  ];

  // Featured pathways (11-13) — full-width items at the bottom of the mega-menu.
  const featuredLinks = [
    { title: 'Internship Pathfinder', href: '/pathways/internships', desc: 'Official government and company internship routes, eligibility and application links.' },
    { title: 'Fresher Launchpad', href: '/pathways/fresher-launchpad', desc: 'Build the skills, confidence and official job-search plan to land your first role.' },
    { title: 'Career Switch & Growth', href: '/pathways/career-switch-growth', desc: 'Switch careers, return after a break or grow through verified official destinations.' },
  ];
  const internshipLink = featuredLinks[0];

  // Tunnel-vision: an onboarded non-admin user only sees their dashboard +
  // AI Mentor + Logout in the navbar — no path/explore/builds/AI-Risk links.
  const isOnboardedUser = false;

  return (
    <>
      {/* Fully-opaque header backdrop — seals the whole header zone (from the
          very top of the viewport incl. iOS safe-area through the bottom of
          the navbar zone) so page content can never bleed through on scroll.
          Height = --lxt-header-h (App.css) which is also where the sticky
          section-pill rows dock, forming one continuous sealed block.
          Sits behind the navbar (z-40 < nav z-50) and above page content. */}
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 z-40 pointer-events-none bg-[#FFF9F1]"
        style={{ height: 'var(--lxt-header-h)' }}
        data-testid="navbar-backdrop"
      />
      {/* <768px: compact full-width docked bar flush under the safe area.
          ≥768px: original floating pill preserved. */}
      <nav className="fixed top-[var(--lxt-ann-h,0px)] left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)] md:top-9 md:px-6 md:pt-3">
        <div className="bg-[#FFF9F1] border-b border-amber-100 px-4 md:max-w-7xl md:mx-auto md:bg-white/80 md:backdrop-blur-md md:border md:border-gray-200/50 md:rounded-full md:shadow-sm md:px-6 md:py-2">
          <div className="flex items-center justify-between h-12 md:h-auto">
            {/* Logo — always links to home (for marketing) or dashboard (for onboarded users) */}
            <Link
              to={isOnboardedUser ? '/dashboard' : '/'}
              className="text-xl md:text-2xl font-bold text-teal-600 hover:text-teal-700 transition-colors"
              data-testid="navbar-logo"
            >
              LearnixTa
            </Link>

            {/* Desktop Navigation — only for anonymous OR admin users */}
            {!isOnboardedUser && (
              <div className="hidden lg:flex items-center space-x-1" data-testid="navbar-public-links">
                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger className="text-sm font-medium" data-testid="navbar-pathways-trigger">Pathways</NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[680px] grid-cols-2 gap-2 p-4" data-testid="navbar-pathways-menu">
                          {pathwaysLinks.map((link) => (
                            <li key={link.href}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={link.href}
                                  data-testid={`navbar-pathway-${link.href.slice(1).replaceAll('/', '-')}`}
                                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                >
                                  <div className="text-sm font-medium leading-none">{link.title}</div>
                                  <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">{link.desc}</p>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                          {featuredLinks.map((fl) => (
                          <li className="col-span-2" key={fl.href}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={fl.href}
                                className="flex items-start gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors bg-teal-50/70 border border-teal-100 hover:bg-teal-100/70 focus:bg-teal-100/70 focus-visible:ring-2 focus-visible:ring-teal-500"
                                data-testid={`navbar-featured-${fl.href.split('/').pop()}`}
                              >
                                <span className="mt-0.5 w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0" aria-hidden="true">
                                  <Briefcase size={15} />
                                </span>
                                <span className="min-w-0">
                                  <span className="flex items-center gap-2">
                                    <span className="text-sm font-medium leading-none">{fl.title}</span>
                                    <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-teal-600 text-white">New</span>
                                  </span>
                                  <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1.5">{fl.desc}</p>
                                </span>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>

                {/* Parents / Partner With Us links temporarily hidden. */}
              </div>
            )}

            {/* Right Side Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              <ThemeToggle />
              {user ? (
                <>
                  {LEXI_ENABLED && (
                  <Link
                    to="/ai-mentor"
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-teal-600 rounded-full hover:bg-gray-100 transition-colors flex items-center gap-1"
                    data-testid="navbar-mentor-link"
                  >
                    <Bot size={16} /> AI Mentor
                  </Link>
                  )}
                  {LEXI_ENABLED && <Link
                    to="/ai-mentor"
                    className="px-3 py-2 text-sm font-medium text-teal-700 hover:text-teal-800 rounded-full hover:bg-teal-50 transition-colors flex items-center gap-1"
                    data-testid="navbar-lexi-status-link"
                  >
                    <Bot size={16} /> Ask Lexi
                  </Link>}
                  <Link
                    to="/my/card"
                    className="px-3 py-2 text-sm font-medium text-teal-700 hover:text-teal-800 rounded-full hover:bg-teal-50 transition-colors flex items-center gap-1"
                    data-testid="navbar-clarity-card-link"
                  >
                    <IdCard size={16} /> My Career Clarity Card
                  </Link>
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-full transition-colors flex items-center gap-2"
                    data-testid="navbar-dashboard-link"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">
                        {user.name?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                    {user.is_admin && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold" data-testid="navbar-admin-badge">
                        <Shield className="w-2.5 h-2.5"/> ADMIN
                      </span>
                    )}
                    Dashboard
                  </Link>
                  {!user.is_guest && (
                  <Link
                    to="/settings"
                    className="p-2 text-gray-400 hover:text-gray-700 transition-colors"
                    title="Account settings"
                    aria-label="Account settings"
                    data-testid="navbar-settings-link"
                  >
                    <Settings size={16} />
                  </Link>
                  )}
                  {!user.is_guest && (
                  <button
                    onClick={logout}
                    className="p-2 text-gray-400 hover:text-gray-700 transition-colors"
                    title="Sign out"
                    aria-label="Sign out"
                    data-testid="logout-button"
                  >
                    <LogOut size={16} />
                  </button>
                  )}
                </>
              ) : user === false ? (
                <>
                  {PUBLIC_SIGN_IN_VISIBLE ? <Link to="/login" className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-full transition-colors" data-testid="navbar-explore-link">
                    Sign in
                  </Link> : LEXI_ENABLED ? <Link to="/ai-mentor" className="px-4 py-2 text-sm font-medium text-white bg-teal-700 rounded-full" data-testid="navbar-guest-lexi">Ask Lexi</Link> : null}
                </>
              ) : (
                <div className="w-20 h-6" />
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              <ThemeToggle className="h-8 w-8" />
              {LEXI_ENABLED && <Link to="/ai-mentor" onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center min-h-[44px] px-3 text-sm font-medium text-white bg-teal-700 rounded-full"
                data-testid="navbar-mobile-guest-lexi">Ask Lexi</Link>}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              data-testid="navbar-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" data-testid="navbar-mobile-menu" className="fixed inset-0 z-40 lg:hidden bg-white overflow-y-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--lxt-ann-h, 0px) + 60px)' }}>
          <div className="px-4 py-6 space-y-4">
            {/* Marketing dropdowns — only for non-onboarded users */}
            {!isOnboardedUser && (
              <>
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900 mb-2">Pathways</h3>
                  {pathwaysLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      data-testid={`navbar-mobile-pathway-${link.href.slice(1).replaceAll('/', '-')}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.title}
                    </Link>
                  ))}
                  {featuredLinks.map((fl) => (
                  <Link
                    key={fl.href}
                    to={fl.href}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-teal-800 bg-teal-50 border border-teal-100 hover:bg-teal-100 rounded-lg mb-1"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`navbar-mobile-${fl.href.split('/').pop()}`}
                  >
                    <Briefcase size={14} aria-hidden="true" /> {fl.title}
                    <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-teal-600 text-white">New</span>
                  </Link>
                  ))}
                </div>

                {/* Parents / Partner With Us links temporarily hidden. */}
              </>
            )}

            <div className="border-t pt-4 space-y-3">
              {user ? (
                <>
                  <Link to="/dashboard" className="block w-full text-center btn-primary" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-mobile-dashboard-link">
                    <LayoutDashboard size={16} className="inline mr-2" /> My Dashboard
                  </Link>
                  {LEXI_ENABLED && (
                  <Link to="/ai-mentor" className="block w-full text-center px-4 py-3 text-sm font-medium text-gray-700 border border-gray-200 rounded-full hover:bg-gray-100" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-mobile-mentor-link">
                    <Bot size={16} className="inline mr-2" /> Ask AI Mentor
                  </Link>
                  )}
                  {!user.is_guest && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); logout(); }}
                    className="block w-full text-center px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700"
                    data-testid="navbar-mobile-logout-btn"
                  >
                    Sign out
                  </button>
                  )}
                </>
              ) : (
                <>
                  {PUBLIC_SIGN_IN_VISIBLE ? <Link to="/login" className="block w-full text-center btn-primary" onClick={() => setMobileMenuOpen(false)} data-testid="navbar-mobile-explore-link">
                    Sign in
                  </Link> : null}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
